import {
  Component,
  Prop,
  Host,
  Element,
  h,
  Event,
  EventEmitter,
  Method,
  Watch,
  Fragment,
  State,
  Listen,
} from "@stencil/core";

import { StzhCalendarChangeEvent, StzhDatepickerChangeEvent } from "../../index";

import { StzhCalendarDateDisabledPredicate } from "../../index";

import { printISODate } from "../../utils/date-utils";
import { StzhLocaleAdapter } from "../../index";

import { StzhDatepickerLocalizedText } from "./stzh-datepicker.localization";
import { hasSlot } from "../../utils/utils";

/**
 * @slot action - Slot for action below calendar (stzh-link element)
 */
@Component({
  tag: "stzh-datepicker",
  styleUrl: "stzh-datepicker.scss",
  scoped: true,
})
export class StzhDatepicker {
  /**
   * Minimum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the max property.
   */
  @Prop() calendarMin: string = "";

  /**
   * Maximum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the min property.
   */
  @Prop() calendarMax: string = "";

  /**
   * Controls which days are disabled and therefore disallowed.
   * For example, this can be used to disallow selection of weekends.
   */
  @Prop() calendarIsDateDisabled: StzhCalendarDateDisabledPredicate = () => false;

  /** Name of the hidden date picker input. */
  @Prop({ reflect: true }) name: string = "";

  /** Value of the hidden date picker input. If a date can be parsed (via picker always), the value is in ISO format. Otherwise the value equals the manual input.  */
  @Prop({ mutable: true }) value: string = "";
  @State() calendarValue: string = "";
  @State() inputValue: string = "";

  /** Default value (used by reset) */
  @Prop({ mutable: true }) defaultValue: string = "";

  /** Label for input field and popover */
  @Prop() label: string = "";

  /** Whether label is visually hidden. */
  @Prop() labelHidden: boolean = false;

  /** Whether the element is readonly or not */
  @Prop({ reflect: true }) readonly: boolean = false;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Hide `(optional)` label (or use `required` inside form to hide it) */
  @Prop({ reflect: true }) hideOptional: boolean = false;

  /** Size variant */
  @Prop({ reflect: true }) size: "default" | "small" = "default";

  /** Show calendar inline */
  @Prop({ reflect: true }) inline: boolean = false;

  /**
   * Date adapter visible date shown to the user, for custom parsing/formatting.
   * Must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.
   */
  @Prop() dateAdapter: StzhLocaleAdapter;

  /**
   * Date adapter for internal date passed to backend, for custom parsing/formatting.
   * Can be `"iso"` (default, date in ISO format) / `"legacy"` (date in `d.m.Y` format)
   * or `StzhLocaleAdapter` (must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.)
   */
  @Prop() internalDateAdapter: "iso" | "legacy" | StzhLocaleAdapter = "iso";
  private _internalDateAdapter: StzhLocaleAdapter;

  /**  Translation strings. */
  @Prop() localization: StzhDatepickerLocalizedText;

  @State() buttonLabel: string;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Watch("localization")
  createDateFormatters() {
    if (this.localization) {
      this.dateFormatLong = new Intl.DateTimeFormat(this.localization.$locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
  }

  /** Update calendar and input when value prop has changed */
  @Watch("value")
  valueWatcher(newValue: string) {
    if (!this._internalDateAdapter) {
      return;
    }

    const date = this._internalDateAdapter.parse(newValue, "dateInput");

    this.calendarValue = printISODate(date);
    this.inputValue = this.dateAdapter.format(date, "dateInput");

    this.updateButtonAccessibleLabel();
  }

  @Watch("dateAdapter")
  dateAdapterWatcher(newValue: StzhLocaleAdapter) {
    if (!newValue) {
      this.dateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formats,
        this.localization.$globals,
      );
    }
  }

  @Watch("internalDateAdapter")
  internalDateAdapterWatcher(newValue: "iso" | "legacy" | StzhLocaleAdapter) {
    if (newValue === "iso" || !newValue) {
      this._internalDateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formatsIso,
        this.localization.$globals,
      );
    } else if (newValue === "legacy") {
      this._internalDateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formatsLegacy,
        this.localization.$globals,
      );
    } else {
      this._internalDateAdapter = newValue;
    }
  }

  /** Set value by a JS Date object */
  @Method()
  async setDate(date: Date) {
    this.value = this._internalDateAdapter.format(date, "dateInput");
  }

  /** Return internal popover element */
  @Method()
  async getPopover(): Promise<HTMLStzhPopoverElement> {
    return this.popover;
  }

  /** Datepicker change event */
  @Event() stzhChange: EventEmitter<StzhDatepickerChangeEvent>;

  @Element() element: HTMLStzhDatepickerElement;

  /**
   * To format dates exclusively for the benefit of screen readers.
   *
   * We prefer DateTimeFormat over date.toLocaleDateString, as the former has
   * better performance when formatting large number of dates. See:
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Performance
   */
  private dateFormatLong: Intl.DateTimeFormat;

  private input: HTMLStzhInputElement;
  private popover: HTMLStzhPopoverElement;
  // private calendar: HTMLStzhCalendarElement;

  private handleReset = async () => {
    this.value = this.defaultValue;
  };

  private onRootFocus = () => {
    if (this.input) {
      this.input.focus();
    }
  };

  private onInputChanged = () => {
    const parsedDate = this.dateAdapter.parse(this.input.value, "dateInput");

    if (this.input.value === "" || parsedDate) {
      const formattedDate = this._internalDateAdapter.format(parsedDate, "dateInput");

      if (this.value !== formattedDate) {
        this.value = formattedDate;
      } else {
        this.valueWatcher(formattedDate);
      }

      this.stzhChange.emit({
        component: "stzh-datepicker",
        value: this.value,
        valueAsDate: parsedDate,
      });
    }
  };

  private onCalendarChange = (event: CustomEvent<StzhCalendarChangeEvent>) => {
    if (event.detail.component !== "stzh-calendar") {
      // Do not emit stzhChange event from year/month dropdowns
      event.stopPropagation();
      return;
    }

    this.value = event.detail.valueAsDate && this._internalDateAdapter.format(event.detail.valueAsDate, "dateInput");

    this.stzhChange.emit({
      component: "stzh-datepicker",
      value: this.value,
      valueAsDate: event.detail.valueAsDate,
    });

    if (this.popover) {
      this.popover.hide();
    }
  };

  private updateButtonAccessibleLabel() {
    if (!this.localization) {
      return;
    }

    try {
      const valueAsDate = this._internalDateAdapter.parse(this.value, "dateInput");
      this.buttonLabel = `${this.label}, ${this.localization.buttonLabel}, ${this.localization.selectedDateMessage} ${this.dateFormatLong.format(valueAsDate)}`;
    } catch (err) {
      this.buttonLabel = `${this.label}, ${this.localization.buttonLabel}`;
    }
  }

  connectedCallback() {
    this.createDateFormatters();
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "datepicker");
    }

    this.dateAdapterWatcher(this.dateAdapter);
    this.internalDateAdapterWatcher(this.internalDateAdapter);
    this.valueWatcher(this.value);
    this.defaultValue = this.defaultValue || this.value;
  }

  render() {
    const actionUsed = hasSlot(this.element, "action");

    const classes = {
      "stzh-datepicker": true,
      "stzh-datepicker--inline": this.inline,
      "stzh-datepicker--has-action": actionUsed,
    };

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        <div class={classes}>
          <input type="hidden" name={this.name} value={this.value} defaultValue={this.defaultValue} />

          {this.inline && (
            <Fragment>
              <stzh-calendar
                // ref={(el) => (this.calendar = el as HTMLStzhCalendarElement)}
                value={this.calendarValue}
                class="stzh-datepicker__calendar is-inline"
                isDateDisabled={this.calendarIsDateDisabled}
                min={this.calendarMin}
                max={this.calendarMax}
                onStzhChange={this.onCalendarChange}
              ></stzh-calendar>
              <div class="stzh-datepicker__actions">
                <slot name="action"></slot>
              </div>
            </Fragment>
          )}

          {!this.inline && (
            <stzh-input
              value={this.inputValue}
              label={this.label}
              labelHidden={this.labelHidden}
              ref={el => (this.input = el as HTMLStzhInputElement)}
              noAutocomplete
              onStzhChanged={this.onInputChanged}
              readonly={this.readonly}
              disabled={this.disabled}
              description={this.description}
              descriptionLong={this.descriptionLong}
              error={this.error}
              invalid={this.invalid}
              required={this.required}
              hideOptional={this.hideOptional}
              size={this.size}
            >
              <stzh-popover
                ref={el => (this.popover = el as HTMLStzhPopoverElement)}
                class="stzh-datepicker__popover"
                slot="button-right"
                placement="bottom-end"
                strategy="fixed"
                label={this.label}
              >
                <stzh-button
                  icon-only
                  icon="calendar"
                  size={this.size}
                  disabled={this.disabled}
                  a11yLabel={this.buttonLabel}
                ></stzh-button>
                <div slot="content">
                  <stzh-calendar
                    // ref={(el) => (this.calendar = el as HTMLStzhCalendarElement)}
                    value={this.calendarValue}
                    class="stzh-datepicker__calendar is-popover"
                    isDateDisabled={this.calendarIsDateDisabled}
                    min={this.calendarMin}
                    max={this.calendarMax}
                    onStzhChange={this.onCalendarChange}
                  ></stzh-calendar>
                  <div class="stzh-datepicker__actions">
                    <slot name="action"></slot>
                  </div>
                </div>
              </stzh-popover>
            </stzh-input>
          )}
        </div>
      </Host>
    );
  }
}
